/* eslint-disable indent */
import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Spinner from 'react-bootstrap/Spinner'
import Card from 'react-bootstrap/Card'
import loginUser from '../services/login'
import { translations } from '../services/arrays'
import { getTranslation } from '../services/translations'
import CreateListModal from './CreateListModal'
import MergeListsModal from './MergeListsModal'
import {
  initializeCandidates,
  addCandidate,
  removeCandidate as removeCandidateFromStore,
} from '../reducers/candidateReducer'
import {
  initializeLists,
  removeList as removeListFromStore,
} from '../reducers/listReducer'
import { initializeBundles } from '../reducers/bundleReducer'
import { changeUser } from '../reducers/userReducer'

const TEAM_TYPE_FILTER_STORAGE_KEY = 'candidateTeamTypeFilter'

const getInitialTeamTypeFilter = () => {
  try {
    const saved = window.localStorage.getItem(TEAM_TYPE_FILTER_STORAGE_KEY)
    return saved === 'MJ' || saved === 'U21' || saved === 'favorites'
      ? saved
      : 'U21'
  } catch {
    return 'U21'
  }
}

const ListsPage = () => {
  const [candidates, setCandidates] = useState([])
  const [lists, setLists] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [activeTab, setActiveTab] = useState('candidates')
  const [manageMode, setManageMode] = useState(false)
  const [teamTypeFilter, setTeamTypeFilter] = useState(getInitialTeamTypeFilter) // 'MJ', 'U21'
  const [selectedU21Tournament, setSelectedU21Tournament] = useState(null)
  const [showAllU21Lists, setShowAllU21Lists] = useState(false)
  const [seasonSortOrder, setSeasonSortOrder] = useState('asc')
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector(state => state.user)
  const lang = (user && user.language) || 'fi'
  const t = key => getTranslation(lang, key)
  const language = lang === 'en' ? 1 : 0
  const bundles = useSelector(state => state.bundles.allBundles)
  const favoriteCandidateIds = new Set(
    (user?.favoriteCandidateLists || []).map(favoriteId => String(favoriteId))
  )

  const formatCandidateName = candidate => {
    const teamPrefix = candidate?.TeamType ? `${candidate.TeamType} ` : ''

    if (
      candidate?.TeamType === 'MJ' &&
      candidate?.Qualifier &&
      candidate.Qualifier.trim()
    ) {
      return `${teamPrefix}${candidate.Name}, ${candidate.Qualifier.trim()}`
    }
    return `${teamPrefix}${candidate?.Name || ''}`.trim()
  }

  const getCompetitionSeasonOrAgeText = candidate => {
    if (candidate?.TeamType === 'MJ') {
      const hasMin = Number.isFinite(candidate?.ageMin)
      const hasMax = Number.isFinite(candidate?.ageMax)

      if (hasMin && hasMax) {
        return candidate.ageMin === candidate.ageMax
          ? `${candidate.ageMin}`
          : `${candidate.ageMin}-${candidate.ageMax}`
      }

      if (hasMin) return `${candidate.ageMin}`
      if (hasMax) return `${candidate.ageMax}`
      return 'N/A'
    }

    if (candidate?.Competition && candidate?.Season) {
      return `${candidate.Competition} ${candidate.Season}`
    }

    return candidate?.Competition || candidate?.Season || 'N/A'
  }

  const getCompetitionRank = competition => {
    const normalized = (competition || '').toUpperCase()
    if (normalized === 'CC') return 0
    if (normalized === 'WC') return 1
    return 2
  }

  const getU21SeasonSortValue = candidate => {
    const season = Number(candidate?.Season)
    return Number.isFinite(season) ? season : Number.MAX_SAFE_INTEGER
  }

  const getU21TournamentKey = candidate =>
    `${(candidate?.Competition || '').toUpperCase()}-${candidate?.Season}`

  const getU21TournamentLabel = candidate =>
    `${(candidate?.Competition || '').toUpperCase()}${candidate?.Season}`

  // TIETOTURVA: Estä pääsy ilman Staff-oikeuksia
  const hasStaffAccess = user && user.staff && user.staff.length > 0

  useEffect(() => {
    // Odotetaan että user.staff on määritelty (ei undefined) ennen tarkistusta
    // Jos user on olemassa JA staff on määritelty JA ei ole Staff-oikeuksia, ohjaa pois
    if (user && user.staff !== undefined && !hasStaffAccess) {
      navigate('/')
    }
  }, [user, hasStaffAccess, navigate])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const loggedUser = await loginUser(dispatch)

        if (loggedUser && loggedUser.oauth_token) {
          // Hae ehdokaslistat Reduxista (tai backendistä jos ei cachessa)
          const candidatesData = await dispatch(
            initializeCandidates(loggedUser.oauth_token)
          )
          setCandidates(candidatesData)

          // Hae vaatimuslistat Reduxista (tai backendistä jos ei cachessa)
          const listsData = await dispatch(
            initializeLists(loggedUser.oauth_token)
          )
          setLists(listsData)

          // Hae niputukset
          await dispatch(initializeBundles(loggedUser.oauth_token))
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [dispatch, user && user.activeNationalTeam])

  useEffect(() => {
    try {
      window.localStorage.setItem(TEAM_TYPE_FILTER_STORAGE_KEY, teamTypeFilter)
    } catch {
      // ignore localStorage errors
    }
  }, [teamTypeFilter])

  useEffect(() => {
    if (teamTypeFilter !== 'U21') {
      setSelectedU21Tournament(null)
      setShowAllU21Lists(false)
    }
  }, [teamTypeFilter])

  // Ehdokaslistan käsittelijät
  const handleCandidateSelection = id => {
    //console.log('handleCandidateSelection called with id:', id)
    if (!id) {
      console.error('No id provided to handleCandidateSelection')
      return
    }
    navigate(`/candidates/${id}`)
  }

  const handleDeleteCandidate = async id => {
    if (window.confirm(t('confirmDeleteCandidate'))) {
      try {
        const loggedUser = window.localStorage.getItem('loggedUser')
        const token = loggedUser ? JSON.parse(loggedUser) : null

        await axios.delete(`/api/candidates/${id}`, {
          headers: {
            'x-oauth-token': token,
          },
        })
        setCandidates(
          candidates.filter(candidate => {
            const candidateId = candidate.id || candidate._id
            return candidateId !== id
          })
        )
        // Päivitä myös Redux-store
        dispatch(removeCandidateFromStore(id))
      } catch (error) {
        console.error('Error deleting candidate:', error)
      }
    }
  }

  const toggleFavoriteCandidateLists = async candidateIds => {
    const ids = candidateIds.map(candidateId => String(candidateId))
    const allAreFavorites = ids.every(candidateId =>
      favoriteCandidateIds.has(candidateId)
    )
    const idsToUpdate = allAreFavorites
      ? ids
      : ids.filter(candidateId => !favoriteCandidateIds.has(candidateId))

    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const oauthToken = loggedUser ? JSON.parse(loggedUser) : null
      let favoriteCandidateLists = user?.favoriteCandidateLists || []

      for (const candidateId of idsToUpdate) {
        const response = allAreFavorites
          ? await axios.delete(`/users/favorite-candidate/${candidateId}`, {
              headers: { 'x-oauth-token': oauthToken },
            })
          : await axios.put(
              `/users/favorite-candidate/${candidateId}`,
              {},
              { headers: { 'x-oauth-token': oauthToken } }
            )
        favoriteCandidateLists = response.data.favoriteCandidateLists
      }

      dispatch(changeUser({ ...user, favoriteCandidateLists }))
    } catch (error) {
      console.error('Error updating candidate list favorites:', error)
    }
  }

  // Vaatimuslistan käsittelijät
  const handleListSelection = id => {
    navigate(`/scoutlist/${id}`)
  }

  const handleDeleteList = async id => {
    if (window.confirm(t('confirmDeleteList'))) {
      try {
        const loggedUser = window.localStorage.getItem('loggedUser')
        const token = loggedUser ? JSON.parse(loggedUser) : null

        await axios.delete(`/list/${id}`, {
          headers: {
            'x-oauth-token': token,
          },
        })
        setLists(
          lists.filter(list => {
            const listId = list.id || list._id
            return listId !== id
          })
        )
        // Päivitä myös Redux-store
        dispatch(removeListFromStore(id))
      } catch (error) {
        console.error('Error deleting list:', error)
      }
    }
  }

  // Uuden listan luonti callback
  const handleListCreated = (type, newList) => {
    if (type === 'candidate') {
      const createdCandidates = Array.isArray(newList) ? newList : [newList]
      setCandidates(prev => [...prev, ...createdCandidates])
      createdCandidates.forEach(candidate => dispatch(addCandidate(candidate)))
    } else if (type === 'requirement') {
      const createdRequirements = Array.isArray(newList) ? newList : [newList]
      setLists(prev => [...prev, ...createdRequirements])
    }
  }

  // Ehdokaslistojen taulukko
  const renderCandidatesTable = () => {
    // Suodata joukkuetyypin mukaan
    let filteredCandidates = candidates.filter(
      c =>
        teamTypeFilter === 'favorites'
          ? favoriteCandidateIds.has(String(c.id || c._id))
          : c.TeamType === teamTypeFilter
    )

    if (
      teamTypeFilter === 'U21' &&
      !selectedU21Tournament &&
      !showAllU21Lists
    ) {
      const tournaments = new Map()

      filteredCandidates.forEach(candidate => {
        if (!candidate?.Competition || !Number.isFinite(Number(candidate?.Season))) {
          return
        }

        const key = getU21TournamentKey(candidate)
        const tournament = tournaments.get(key) || {
          key,
          label: getU21TournamentLabel(candidate),
          season: getU21SeasonSortValue(candidate),
          competition: candidate.Competition,
          count: 0,
        }
        tournament.count += 1
        tournaments.set(key, tournament)
      })

      const sortedTournaments = [...tournaments.values()].sort((a, b) => {
        const seasonDiff = a.season - b.season
        if (seasonDiff !== 0) return seasonDiff
        return getCompetitionRank(a.competition) - getCompetitionRank(b.competition)
      })

      return (
        <Table striped bordered variant="dark" size="sm" hover>
          <thead>
            <tr>
              <th>Kisa/kausi</th>
              <th>{t('candidateLists')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedTournaments.length > 0 ? (
              sortedTournaments.map(tournament => (
                <tr
                  key={tournament.key}
                  onClick={() => {
                    setShowAllU21Lists(false)
                    setSelectedU21Tournament(tournament.key)
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{tournament.label}</td>
                  <td>{tournament.count}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="text-center">
                  {t('noCandidateLists')}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )
    }

    if (teamTypeFilter === 'U21' && selectedU21Tournament) {
      filteredCandidates = filteredCandidates.filter(
        candidate => getU21TournamentKey(candidate) === selectedU21Tournament
      )
    }

    // U21-listat järjestetään aina kauden mukaan, ja saman kauden sisällä CC ennen WC.
    if (teamTypeFilter === 'U21') {
      filteredCandidates = [...filteredCandidates].sort((a, b) => {
        const seasonA = getU21SeasonSortValue(a)
        const seasonB = getU21SeasonSortValue(b)
        const seasonDiff =
          seasonSortOrder === 'asc' ? seasonA - seasonB : seasonB - seasonA

        if (seasonDiff !== 0) return seasonDiff

        const compDiff =
          getCompetitionRank(a?.Competition) -
          getCompetitionRank(b?.Competition)
        if (compDiff !== 0) return compDiff

        return formatCandidateName(a).localeCompare(
          formatCandidateName(b),
          'fi',
          {
            sensitivity: 'base',
          }
        )
      })
    }

    // Ryhmittele niputetut ja niputtamattomat
    const bundledIds = new Set()
    const rows = []

    // Lisää niputetut rivit
    for (const bundle of bundles) {
      const bundleCandidates = bundle.candidates
        .map(bc => {
          const bcId = bc.id || bc._id || bc
          return filteredCandidates.find(c => (c.id || c._id) === bcId)
        })
        .filter(Boolean)

      if (bundleCandidates.length > 0) {
        bundleCandidates.forEach(c => bundledIds.add(c.id || c._id))
        // Näytä niputettu rivi
        const names = bundleCandidates.map(c => formatCandidateName(c))
        const displayName =
          names.length <= 1
            ? names[0] || ''
            : names.slice(0, -1).join(', ') + ' ja ' + names[names.length - 1]
        const firstCandidate = bundleCandidates[0]
        const firstId = firstCandidate.id || firstCandidate._id
        const competitionSeasonText =
          getCompetitionSeasonOrAgeText(firstCandidate)
        const totalPlayers = bundleCandidates.reduce(
          (sum, c) =>
            sum + (c.playersCount ?? (c.Players ? c.Players.length : 0)),
          0
        )

        rows.push({
          type: 'bundle',
          key: `bundle-${bundle.id}`,
          id: firstId,
          bundleId: bundle.id,
          name: displayName,
          competitionSeason: competitionSeasonText,
          playersCount: totalPlayers,
          candidateIds: bundleCandidates.map(c => c.id || c._id),
        })
      }
    }

    // Lisää niputtamattomat rivit
    for (const candidate of filteredCandidates) {
      const candidateId = candidate.id || candidate._id
      if (!bundledIds.has(candidateId)) {
        const competitionSeasonText = getCompetitionSeasonOrAgeText(candidate)
        rows.push({
          type: 'single',
          key: candidateId,
          id: candidateId,
          name: formatCandidateName(candidate),
          competitionSeason: competitionSeasonText,
          playersCount:
            candidate.playersCount ??
            (candidate.Players ? candidate.Players.length : 0),
          candidateIds: [candidateId],
        })
      }
    }

    if (teamTypeFilter === 'U21') {
      rows.sort((a, b) => {
        const candidateA =
          a.type === 'bundle'
            ? filteredCandidates.find(c => (c.id || c._id) === a.id)
            : filteredCandidates.find(c => (c.id || c._id) === a.id)
        const candidateB =
          b.type === 'bundle'
            ? filteredCandidates.find(c => (c.id || c._id) === b.id)
            : filteredCandidates.find(c => (c.id || c._id) === b.id)

        const seasonA = getU21SeasonSortValue(candidateA)
        const seasonB = getU21SeasonSortValue(candidateB)
        const seasonDiff =
          seasonSortOrder === 'asc' ? seasonA - seasonB : seasonB - seasonA

        if (seasonDiff !== 0) return seasonDiff

        const compDiff =
          getCompetitionRank(candidateA?.Competition) -
          getCompetitionRank(candidateB?.Competition)
        if (compDiff !== 0) return compDiff

        return (a.name || '').localeCompare(b.name || '', 'fi', {
          sensitivity: 'base',
        })
      })
    }

    return (
      <Table striped bordered variant="dark" size="sm" hover>
        <thead>
          <tr>
            <th>{t('name')}</th>
            <th
              onClick={() => {
                if (teamTypeFilter === 'U21') {
                  setSeasonSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
                }
              }}
              style={{
                cursor: teamTypeFilter === 'U21' ? 'pointer' : 'default',
                userSelect: 'none',
              }}
            >
              {teamTypeFilter === 'MJ'
                ? 'Ikä'
                : `Kisa/Kausi ${seasonSortOrder === 'asc' ? '▲' : '▼'}`}
            </th>
            <th>{t('players')}</th>
            <th>Suosikki</th>
            {manageMode && <th>{t('action')}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map(row => (
              <tr
                key={row.key}
                onClick={() => !manageMode && handleCandidateSelection(row.id)}
                style={{ cursor: manageMode ? 'default' : 'pointer' }}
              >
                <td>
                  {row.type === 'bundle' ? (
                    <span>📦 {row.name}</span>
                  ) : (
                    row.name
                  )}
                </td>
                <td>{row.competitionSeason}</td>
                <td>{row.playersCount}</td>
                <td className="text-end">
                  <Button
                    variant={
                      row.candidateIds.every(candidateId =>
                        favoriteCandidateIds.has(String(candidateId))
                      )
                        ? 'info'
                        : 'outline-info'
                    }
                    size="sm"
                    style={{ minWidth: '30px', padding: '0.1rem 0.3rem' }}
                    aria-label={
                      row.candidateIds.every(candidateId =>
                        favoriteCandidateIds.has(String(candidateId))
                      )
                        ? 'Poista suosikeista'
                        : 'Lis\u00e4\u00e4 suosikiksi'
                    }
                    title={
                      row.candidateIds.every(candidateId =>
                        favoriteCandidateIds.has(String(candidateId))
                      )
                        ? 'Poista suosikeista'
                        : 'Lis\u00e4\u00e4 suosikiksi'
                    }
                    onClick={event => {
                      event.stopPropagation()
                      toggleFavoriteCandidateLists(row.candidateIds)
                    }}
                  >
                    {row.candidateIds.every(candidateId =>
                      favoriteCandidateIds.has(String(candidateId))
                    )
                      ? '\u2605'
                      : '\u2606'}
                  </Button>
                </td>
                {manageMode && (
                  <td>
                    {row.type === 'single' ? (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation()
                          handleDeleteCandidate(row.id)
                        }}
                      >
                        {t('remove')}
                      </Button>
                    ) : (
                      <span style={{ color: '#888', fontSize: '0.8rem' }}>
                        {lang === 'fi'
                          ? 'Pura niputus ensin'
                          : 'Unbundle first'}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={manageMode ? 5 : 4} className="text-center">
                {t('noCandidateLists')}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    )
  }

  // Vaatimuslistojen taulukko
  const renderListsTable = () => (
    <Table striped bordered variant="dark" size="sm" hover>
      <thead>
        <tr>
          <th>{t('name')}</th>
          <th>{t('age')}</th>
          <th>HTMS</th>
          <th>{t('requirements')}</th>
          {manageMode && <th>{t('action')}</th>}
        </tr>
      </thead>
      <tbody>
        {lists.length > 0 ? (
          lists.map(list => (
            <tr
              key={list.id}
              onClick={() => !manageMode && handleListSelection(list.id)}
              style={{ cursor: manageMode ? 'default' : 'pointer' }}
            >
              <td>{list.Name}</td>
              <td>{list.Age || 'N/A'}</td>
              <td>{list.HTMS || 'N/A'}</td>
              <td>
                {list.Skills
                  ? Object.entries(list.Skills).map(([key, value]) => (
                      <span key={key} className="me-2">
                        {translations[key] ? translations[key][language] : key}:{' '}
                        {value}
                      </span>
                    ))
                  : 'N/A'}
              </td>
              {manageMode && (
                <td>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation()
                      handleDeleteList(list.id)
                    }}
                  >
                    {t('remove')}
                  </Button>
                </td>
              )}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={manageMode ? 5 : 4} className="text-center">
              {t('noRequirementLists')}
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  )

  if (loading) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" variant="light" />
        <p className="text-light mt-2">{t('loadingLists')}</p>
      </div>
    )
  }

  // TIETOTURVA: Näytä viesti jos ei Staff-oikeuksia
  // Mutta vain jos user.staff on määritelty (ei undefined)
  if (user && user.staff !== undefined && !hasStaffAccess) {
    return (
      <Card bg="dark" text="light" className="mt-3">
        <Card.Body className="text-center">
          <h4>{t('accessDenied')}</h4>
          <p>{t('staffOnlyPage')}</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            {t('returnToHome')}
          </Button>
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card bg="dark" text="light" className="mt-3 lists-page-card">
      <Card.Header className="d-flex justify-content-between align-items-center lists-page-header">
        <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
          {t('lists')}
        </span>
        <div className="lists-page-header-actions">
          <Button
            variant="outline-info"
            className="me-2"
            onClick={() => navigate('/targetlevels')}
          >
            {t('targetLevels')}
          </Button>
          <Button
            variant="outline-light"
            className="me-2"
            onClick={() => setShowMergeModal(true)}
          >
            {t('mergeLists')}
          </Button>
          <Button
            variant={manageMode ? 'warning' : 'outline-warning'}
            className="me-2"
            onClick={() => setManageMode(!manageMode)}
          >
            {manageMode ? t('stopManaging') : t('manageMode')}
          </Button>
          <Button variant="success" onClick={() => setShowCreateModal(true)}>
            {t('createNewList')}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="mb-3 lists-page-tabs">
          <ButtonGroup className="lists-page-tab-group">
            <Button
              variant={activeTab === 'candidates' ? 'info' : 'secondary'}
              onClick={() => setActiveTab('candidates')}
            >
              {t('candidateLists')} ({candidates.length})
            </Button>
            <Button
              variant={activeTab === 'requirements' ? 'info' : 'secondary'}
              onClick={() => setActiveTab('requirements')}
            >
              {t('requirementLists')} ({lists.length})
            </Button>
          </ButtonGroup>
        </div>

        {activeTab === 'candidates' && (
          <div className="mb-3 d-flex gap-2 align-items-center lists-page-team-filter">
            <ButtonGroup className="lists-page-team-toggle">
              <Button
                variant={
                  teamTypeFilter === 'MJ' ? 'primary' : 'outline-primary'
                }
                size="sm"
                onClick={event => {
                  setTeamTypeFilter('MJ')
                  setSelectedU21Tournament(null)
                  setShowAllU21Lists(false)
                  event.currentTarget.blur()
                }}
              >
                MJ
              </Button>
              <Button
                variant={
                  teamTypeFilter === 'U21' ? 'primary' : 'outline-primary'
                }
                size="sm"
                onClick={event => {
                  setTeamTypeFilter('U21')
                  setSelectedU21Tournament(null)
                  setShowAllU21Lists(false)
                  event.currentTarget.blur()
                }}
              >
                U21
              </Button>
              <Button
                variant={
                  teamTypeFilter === 'favorites'
                    ? 'primary'
                    : 'outline-primary'
                }
                size="sm"
                aria-label="Suosikit"
                title="Suosikit"
                onClick={event => {
                  setTeamTypeFilter('favorites')
                  setSelectedU21Tournament(null)
                  setShowAllU21Lists(false)
                  event.currentTarget.blur()
                }}
              >
                {'\u2605'}
              </Button>
            </ButtonGroup>
            {teamTypeFilter === 'U21' &&
              !selectedU21Tournament &&
              !showAllU21Lists && (
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => setShowAllU21Lists(true)}
                >
                  {lang === 'fi'
                    ? 'N\u00e4yt\u00e4 kaikki listat'
                    : 'Show all lists'}
                </Button>
              )}
            {teamTypeFilter === 'U21' &&
              (selectedU21Tournament || showAllU21Lists) && (
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => {
                  setSelectedU21Tournament(null)
                  setShowAllU21Lists(false)
                }}
              >
                {lang === 'fi' ? 'Kaikki kisat' : 'All tournaments'}
              </Button>
            )}
          </div>
        )}

        <div className="lists-page-table-wrap">
          {activeTab === 'candidates'
            ? renderCandidatesTable()
            : renderListsTable()}
        </div>
      </Card.Body>

      <CreateListModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onListCreated={handleListCreated}
        defaultType={activeTab === 'candidates' ? 'candidate' : 'requirement'}
        user={user}
      />

      <MergeListsModal
        show={showMergeModal}
        onHide={() => setShowMergeModal(false)}
        candidates={candidates}
        onBundlesChanged={() => {}}
      />
    </Card>
  )
}

export default ListsPage
